import { NgClass, NgIf, NgStyle } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { BaseService, GetFieldValuePipe } from '@shared/core';
import { LocalizationPipe } from '@shared/modules/localization';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzOptionComponent, NzSelectComponent, NzSelectModule } from 'ng-zorro-antd/select';
import { UILoading } from '../loading';

interface IFormSelectFilter {
  [key: string]: unknown;
  pageNumber: number;
  pageSize: number;
}

@Component({
  selector: 'ui-multli-select-list',
  templateUrl: './multli-select-list.component.html',
  styleUrl: './multli-select-list.component.scss',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    NgIf,
    NgClass,
    NgStyle,
    NzFormModule,
    NzSelectModule,
    FormsModule,
    GetFieldValuePipe,
    LocalizationPipe,
    UILoading,
    ReactiveFormsModule,
  ],
  providers: [
    GetFieldValuePipe,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UIMultiSelectList),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => UIMultiSelectList),
      multi: true,
    },
  ],
})
export class UIMultiSelectList implements ControlValueAccessor, Validator {
  @Input() placeholder?: string;
  @Input() requestUrl?: string;
  @Input() requestParams?: { [key: string]: unknown } = {};
  @Input() options?: any[];
  @Input() optionLabelField?: string;
  @Input() optionValueField?: string;
  @Input() showSearch?: boolean;
  @Input() serverSearch?: boolean;
  @Input() searchField?: string;
  @Input() pageable?: boolean;
  @Input() disabled?: boolean;
  @Input() hideSelector?: boolean;

  @Input() selectedValueList?: any[] = [];
  control?: AbstractControl;

  @Output() selectedValueListChange = new EventEmitter<any[]>();

  isLoadingData = false;
  isLoadingMore = false;

  @ViewChild('selectRef', { read: NzSelectComponent, static: false })
  selectRef?: NzSelectComponent;
  @ViewChildren(NzOptionComponent) optionsList?: QueryList<NzOptionComponent>;

  private get optionFilter(): IFormSelectFilter {
    return Object.assign({
      ...this.requestParams,
      pageNumber: 1,
      pageSize: 20,
    });
  }

  private getFieldValuePipe = inject(GetFieldValuePipe);
  private httpService = inject(BaseService);
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = inject(DestroyRef);

  onSelectOpened(isOpen: boolean): void {
    if (isOpen) {
      this.fetchOptions();
    }
  }

  isSelected(value: any): boolean {
    return (
      this.selectedValueList?.findIndex(
        item =>
          this.getFieldValuePipe.transform(item, this.optionValueField) ===
          this.getFieldValuePipe.transform(value, this.optionValueField),
      ) !== -1
    );
  }

  // Fetch options from the server
  private fetchOptions(): void {
    if (!this.requestUrl || this.isLoadingData || this.isLoadingMore) {
      return;
    }
    this.isLoadingData = true;
    // Implementation for fetching options from the server
    this.httpService
      .get(this.requestUrl, this.optionFilter, 'json', true)
      .pipe(takeUntilDestroyed(this.destroy$))
      .subscribe(response => {
        this.options = response?.value?.content ?? response?.value ?? [];
        this.isLoadingData = false;
        this.isLoadingMore = false;
        this.cdr.detectChanges();
      });
  }

  onSearch(searchText: string): void {
    if (!this.searchField) {
      return;
    }
    // Implement your search logic here
    this.optionFilter[this.searchField] = searchText;
    this.optionFilter.pageNumber = 1;
    this.fetchOptions();
  }

  loadMore(): void {
    if (!this.pageable || this.isLoadingMore) {
      return;
    }
    this.isLoadingMore = true;
    // Implement your load more logic here
    if (this.optionFilter?.pageNumber) {
      this.optionFilter.pageNumber++;
    } else {
      this.optionFilter.pageNumber = 1;
    }
    this.fetchOptions();
  }

  // Handles changes to the value
  onValueChange(value: any) {
    this.closeSelectDropdownIfItIsLastOption();
    this.selectedValueList = value;
    this.onChange(this.selectedValueList);
    this.selectedValueListChange.emit(this.selectedValueList);
    this.updateControlValidity(); // Ensure control state is updated
  }

  closeSelectDropdownIfItIsLastOption() {
    if (this.optionsList?.length === 1 && this.selectRef) {
      this.selectRef.nzOpen = false;
    }
  }

  onRemoveSelectedValue(index: number): void {
    if (this.disabled) {
      return;
    }
    this.selectedValueList?.splice(index, 1);
    this.selectedValueListChange.emit(this.selectedValueList);
  }

  protected onValidatorChange: () => void = () => void {};
  protected onChange: (value: any) => void = () => void {};
  protected onTouched: () => void = () => void {};

  // ControlValueAccessor methods
  writeValue(value: any): void {
    this.selectedValueList = value ?? [];
    this.updateControlValidity();
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn; // Store the callback function
  }

  protected updateControlValidity(): void {
    if (this.control) {
      this.control.setErrors(null);
      this.control.updateValueAndValidity({ emitEvent: false });
    }
    // Notify Angular about the validator change
    this.onValidatorChange();
  }
  // Validator method
  validate(control: AbstractControl): ValidationErrors | null {
    this.control = control; // Keep a reference to the control
    return control.errors; // Return the errors from the control itself
  }

  readonly compareFn = (o1: any, o2: any): boolean => {
    return false;
  };

  trackById(index: number, item: any) {
    return this.getFieldValuePipe?.transform(item, this.optionValueField);
  }
}
